import { memo } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

/**
 * FilterGroup - Renders a group of filter chips with All/None controls
 * @param {string} label - Group label
 * @param {string} groupKey - Key in the filter state object
 * @param {string[]} keys - Array of filter options
 * @param {function} colorFor - Optional function to get color for a chip
 * @param {function} renderChip - Optional custom chip renderer
 * @param {object} value - Current filter state
 * @param {function} setAll - Function to select all options
 * @param {function} clearAll - Function to clear all selections
 * @param {function} toggle - Function to toggle individual option
 * @param {boolean} disableClosedLost - Whether to disable "Closed Lost" option
 */
const FilterGroup = memo(({
  label,
  groupKey,
  keys,
  colorFor,
  renderChip,
  value,
  setAll,
  clearAll,
  toggle,
  disableClosedLost
}) => {
  const theme = useTheme();

  // Normalize to Set
  let selectedRaw = value?.[groupKey];
  if (!(selectedRaw instanceof Set)) {
    selectedRaw = new Set(Array.isArray(selectedRaw) ? selectedRaw : []);
  }
  const selected = selectedRaw;
  const allSelected = keys.every(k => selected.has(k));
  const showingKeys = keys;
  const summary = `${selected.size} of ${keys.length} selected`;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.5,
        px: 0.75,
        py: 0.5,
        pr: 1,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fafafa',
        minHeight: 34,
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          borderColor: '#c0c0c0',
          background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5',
        }
      }}
    >
      <Typography
        variant='caption'
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          mr: 0.75,
          color: theme.palette.text.primary,
          textTransform: 'uppercase'
        }}
      >
        {label}
      </Typography>
      <Tooltip title={`Select all ${label.toLowerCase()}`} enterDelay={200} leaveDelay={200} arrow>
        <Chip
          size='small'
          label='All'
          sx={{ height: 24 }}
          variant={allSelected ? 'filled' : 'outlined'}
          onClick={() => setAll(groupKey, keys)}
        />
      </Tooltip>
      <Tooltip title={`Clear all ${label.toLowerCase()}`} enterDelay={200} leaveDelay={200} arrow>
        <Chip
          size='small'
          label='None'
          sx={{ height: 24 }}
          variant={selected.size === 0 ? 'filled' : 'outlined'}
          onClick={() => clearAll(groupKey)}
        />
      </Tooltip>
      {showingKeys.map(k =>
        renderChip ? (
          renderChip(k, selected.has(k), () => toggle(groupKey, k))
        ) : (
          <Chip
            key={k}
            size='small'
            label={k}
            variant={selected.has(k) ? 'filled' : 'outlined'}
            color={colorFor ? colorFor(k) : undefined}
            disabled={groupKey === 'statuses' && disableClosedLost && k === 'Closed Lost'}
            onClick={() => toggle(groupKey, k)}
            sx={{ height: 24 }}
          />
        )
      )}
      <Typography
        variant='caption'
        sx={{
          fontSize: 9,
          color: theme.palette.text.secondary,
          ml: 'auto',
          fontStyle: 'italic',
          pl: 0.5
        }}
      >
        {summary}
      </Typography>
    </Box>
  );
});

FilterGroup.displayName = 'FilterGroup';

export default FilterGroup;
